/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { IDBPDatabase, openDB, OpenDBCallbacks } from "idb";
import { EvalStore, evalStoreConfig as evalsStore, EVALS_STORE, EVALS_TO_CREATE_STORE } from "./stores/evals";
import { FormStore, formStoreConfig, FORMS_STORE } from "./stores/forms";
import { ImagesStore, imageStoreConfig, IMAGE_STORE, OfflineImageUpdateStore, offlineImageUpdateStoreConfig, OFFLINE_IMAGE_UPDATE_STORE } from "./stores/images";
import { LIVE_EVAL_PATCHES_STORE, PatchStore, patchStoreConfig as patchStore, PENDING_EVAL_PATCHES_STORE } from "./stores/patches";
import { UserStore, userStoreConfig } from "./stores/users";
import { FacilitiesStore, facilitiesStoreConfig, FACILITIES_STORE } from "./stores/facilities";
import { COOPS_STORE, coopStoreConfig, CoopsStore } from "./stores/coops";
import { WMPS_STORE, wmpStoreConfig, WmpsStore } from "./stores/wmps";

const CURRENT_DATABASE_VERSION = 9;

export type DairyFARMDB = EvalStore & PatchStore & ImagesStore & FormStore & OfflineImageUpdateStore & UserStore & FacilitiesStore & CoopsStore & WmpsStore;

let idb: IDBPDatabase<DairyFARMDB>;

const returnDB: () => Promise<IDBPDatabase<DairyFARMDB>> = () => Promise.resolve(idb);

const loadDB: () => Promise<IDBPDatabase<DairyFARMDB>> = () =>
  openDB<DairyFARMDB>("FARM-Eval", CURRENT_DATABASE_VERSION, storeConfig).then(db => {
    idb = db;
    dbLoader = returnDB;
    return idb;
  });

let dbLoader = loadDB;

export function getDB() {
  return dbLoader();
}

function combineStores(...stores: OpenDBCallbacks<DairyFARMDB>[]): OpenDBCallbacks<DairyFARMDB> {
  return {
    upgrade: (db, oldVersion, newVersion, transaction) =>
      stores.forEach(store => {
        if (store.upgrade) store.upgrade(db, oldVersion, newVersion, transaction);
      }),
  };
}

export const storeConfig = combineStores(
  evalsStore,
  userStoreConfig,
  patchStore,
  imageStoreConfig,
  formStoreConfig,
  offlineImageUpdateStoreConfig,
  facilitiesStoreConfig,
  coopStoreConfig,
  wmpStoreConfig
);

export const storeNames = {
  EVALS_STORE,
  EVALS_TO_CREATE_STORE,
  LIVE_EVAL_PATCHES_STORE,
  PENDING_EVAL_PATCHES_STORE,
  IMAGE_STORE,
  FORMS_STORE,
  OFFLINE_IMAGE_UPDATE_STORE,
  FACILITIES_STORE,
  COOPS_STORE,
  WMPS_STORE
};

// type WRT<T> = (db: () => T) => Promise<T>

// fireauth.storage.IndexedDB.prototype.withRetry_ = function(transaction) {
export const withRetry_ = function<T>(transaction: any): Promise<T> {
  let numAttempts = 0;
  const attempt = (resolve: any, reject: any) => {
      getDB()
      .then(function (arg) {
        return transaction(arg);
      })
      .then(resolve)
      .catch((error: any) => {
        if (++numAttempts > 10) {
          reject(error);
          return;
        }
        return getDB().then((db) => {
          db.close();
          dbLoader = loadDB;
          return attempt(resolve, reject);
        });
      });
  };
  return new Promise(attempt);
}
